import axios from "axios";
import { enqueue, installAutoFlush, isNetworkError } from "./syncQueue";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  // 15 s → after that we treat it as offline and queue mutations
  timeout: 20_000,
});

const TOKEN_KEY = "hrms_access_token";

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Offline-queue interceptor: if a mutation fails due to a network error,
// enqueue it for later replay and surface a friendly "queued" response
// (so UI code that expects data.ok / data.id still behaves sensibly).
const QUEUEABLE = new Set(["post", "put", "patch", "delete"]);

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config;
    if (!cfg || cfg._isReplay || cfg._skipQueue) throw error;
    const method = (cfg.method || "").toLowerCase();
    if (!QUEUEABLE.has(method) || !isNetworkError(error)) throw error;
    // Never queue file uploads (multipart bodies) — the FormData reference is gone
    // by the time we'd replay it. Advise the user to retry once back online.
    const isMultipart = (cfg.headers?.["Content-Type"] || cfg.headers?.["content-type"] || "").toString().includes("multipart");
    if (isMultipart) throw error;
    try {
      // Serialize body — axios stringifies JSON before this point
      let body = cfg.data;
      if (typeof body === "string") { try { body = JSON.parse(body); } catch { /* keep string */ } }
      await enqueue({
        method,
        url: cfg.url,
        body,
        headers: {
          "Content-Type": "application/json",
          ...(cfg.headers?.Authorization ? { Authorization: cfg.headers.Authorization } : {}),
        },
      });
    } catch (_) { /* ignore queue errors — still throw the original */ }
    // Return a synthetic "queued" success so calling code doesn't crash
    return {
      data: { ok: true, queued: true, offline: true },
      status: 202,
      statusText: "Queued (offline)",
      headers: {},
      config: cfg,
      _queued: true,
    };
  }
);

installAutoFlush(api);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}
